import { useCallback, useRef, useState } from 'react';
import { apiUrl } from '../api';

/**
 * Initial phase state for tracking analysis progress
 */
const INITIAL_PHASES = {
  content_analysis: { status: 'pending', display: 'Content Analysis' },
  engagement_analysis: { status: 'pending', display: 'Engagement Analysis' },
  recommendations: { status: 'pending', display: 'Challenges & Recommendations' }
};

const SECTION_ORDER = ['content_analysis', 'engagement_analysis', 'recommendations'];

/**
 * Custom hook for SSE-based transcript analysis.
 *
 * Uses fetch + ReadableStream (instead of EventSource) to support
 * Authorization headers for authenticated requests.
 *
 * @returns {Object} Hook state and methods
 */
export function useSSEAnalysis() {
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [phases, setPhases] = useState(INITIAL_PHASES);
  const abortControllerRef = useRef(null);

  /**
   * Start analyzing a transcript via SSE streaming.
   * @param {number} transcriptId - The transcript ID to analyze
   */
  const startAnalysis = useCallback(async (transcriptId, action = 'google_meet.analyze_stream') => {
    // Get auth token
    const token = localStorage.getItem('lrbrain-site-token');

    // Reset state
    setIsAnalyzing(true);
    setProgress({ stage: 'connecting', percentage: 0, message: 'Connecting...' });
    setError(null);
    setSummary(null);
    setPhases(INITIAL_PHASES);

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(
        apiUrl(`/api/google/meet/transcripts/${transcriptId}/analyze-stream`),
        {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
            'X-LRB-User-Action': action
          },
          signal: abortControllerRef.current.signal
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Analysis failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6);
              const event = JSON.parse(jsonStr);

              switch (event.type) {
                case 'progress':
                  setProgress(event.data);
                  break;
                case 'section_started':
                  if (event.data?.section_key) {
                    setPhases(prev => ({
                      ...prev,
                      [event.data.section_key]: {
                        ...prev[event.data.section_key],
                        status: 'in_progress',
                        error: null
                      }
                    }));
                  }
                  break;
                case 'section_complete':
                  if (event.data?.section_key) {
                    setPhases(prev => ({
                      ...prev,
                      [event.data.section_key]: {
                        ...prev[event.data.section_key],
                        status: 'complete',
                        error: null
                      }
                    }));
                  }
                  break;
                case 'section_failed':
                  if (event.data?.section_key) {
                    setPhases(prev => ({
                      ...prev,
                      [event.data.section_key]: {
                        ...prev[event.data.section_key],
                        status: 'error',
                        error: event.data.error
                      }
                    }));
                  }
                  setProgress(prev => ({
                    ...(prev || {}),
                    message: event.data?.error || 'A section failed. Retry to complete analysis.',
                    hasWarning: true
                  }));
                  break;
                case 'summary_state': {
                  const nextSummary = event.data?.summary;
                  if (nextSummary) {
                    setSummary(nextSummary);
                    const completed = Array.isArray(nextSummary.completed_sections) ? nextSummary.completed_sections.length : 0;
                    const percentage = Math.min(95, 10 + Math.round((completed / SECTION_ORDER.length) * 80));
                    setProgress(prev => ({
                      stage: 'analyzing',
                      percentage,
                      message: `Analyzing sections (${completed}/${SECTION_ORDER.length})...`
                    }));
                  }
                  break;
                }
                case 'phase_error':
                  // Backward compatibility with legacy event format
                  if (event.data.phase) {
                    setPhases(prev => ({
                      ...prev,
                      [event.data.phase]: {
                        ...prev[event.data.phase],
                        status: 'error',
                        error: event.data.error
                      }
                    }));
                  }
                  setProgress(prev => ({
                    ...(prev || {}),
                    message: event.data.message,
                    hasWarning: true
                  }));
                  break;
                case 'summary':
                  setSummary(event.data);
                  break;
                case 'done':
                  setIsAnalyzing(false);
                  break;
                case 'error':
                  setError(event.data.message);
                  setIsAnalyzing(false);
                  break;
                default:
                  console.warn('Unknown SSE event type:', event.type);
              }
            } catch (parseError) {
              console.error('Failed to parse SSE event:', parseError, line);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // User cancelled - don't treat as error
        setProgress({ stage: 'cancelled', percentage: 0, message: 'Analysis cancelled' });
      } else {
        setError(err.message);
      }
      setIsAnalyzing(false);
    }
  }, []);

  /**
   * Cancel an in-progress analysis.
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsAnalyzing(false);
  }, []);

  /**
   * Reset the hook state for a new analysis.
   */
  const reset = useCallback(() => {
    setProgress(null);
    setSummary(null);
    setError(null);
    setIsAnalyzing(false);
    setPhases(INITIAL_PHASES);
  }, []);

  return {
    startAnalysis,
    cancel,
    reset,
    progress,
    summary,
    error,
    isAnalyzing,
    phases
  };
}

export default useSSEAnalysis;
